<template>
  <div class="w-32 z-[100] dropdown mt-1" aria-label="Country Area code">
    <Combobox v-model="selected">
      <div class="relative">
        <div
            class="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm"
        >
          <ComboboxInput
              class="w-full h-[52px] border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 outline-none"
              :displayValue="() => filteredCountries.find(el => el.fips === selected)?.phoneCode"
              @change="(e) => {
                query = e.target.value
              }"
          />

          <CountryFlag
              :country="selected"
              :class="['top-[7px] absolute', rtl ? 'right-[80px]' : 'right-[45px]']"
          />

          <ComboboxButton
              class="absolute inset-y-0 right-0 flex items-center pr-2"
          >
            <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
            />
          </ComboboxButton>
        </div>
        <TransitionRoot
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            @after-leave="query = ''"
        >
          <ComboboxOptions
              class="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
          >
            <div
                v-if="filteredCodes.length === 0 && query !== ''"
                class="relative cursor-default select-none py-2 px-4 text-gray-700"
            >
              {{ rtl ? 'שום דבר לא נמצא' : 'Nothing found' }}
            </div>

            <ComboboxOption
                v-for="({fips, phoneCode}, index) in filteredCodes"
                as="template"
                :key="index"
                :value="fips"
                v-slot="{ selected, active }"
            >
              <li
                  class="relative cursor-default select-none py-2 pl-10 pr-4"
                  :class="{
                  'bg-main text-white': active,
                  'text-gray-900': !active,
                }"
              >
                <span
                    class="block truncate"
                    :class="{ 'font-medium': selected, 'font-normal': !selected }"
                >
                  <CountryFlag
                      :country="fips"
                      :class="['bottom-[8px] absolute', rtl ? 'right-[60px]' : 'right-[15px]']"
                  />
                  +{{ phoneCode }}
                </span>
                <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center pl-3"
                    :class="{ 'text-white': active, 'text-main': !active }"
                >
                  <CheckIcon class="h-5 w-5" aria-hidden="true"/>
                </span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>
  </div>
</template>

<script setup lang="ts">
import CountryFlag from "vue-country-flag-next"
import {ref, computed, PropType} from 'vue'
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue'
import {CheckIcon, ChevronUpDownIcon} from '@heroicons/vue/20/solid'
import {countryCode, ICountryCodes} from "~/config/countryArea.js";

const emit = defineEmits({
  change: null
})

defineProps({
  rtl: {
    type: Boolean as PropType<boolean>,
    default: false
  }
})

const filteredCountries = computed<ICountryCodes[]>(() => {
  return countryCode
      .filter((_, i) => i % 2 === 0)
      .sort((a, b) => a.phoneCode - b.phoneCode)
})

let selected = ref("IL")
let query = ref('972')

let filteredCodes = computed(() => {
      if (query.value.length === 0) {
        return filteredCountries.value
      }
      return filteredCountries.value
          .filter(({phoneCode}) =>
              phoneCode
                  .toString()
                  .includes(query.value)
          )
          .sort((a, b) => a - b)
    }
)

watch(selected, () => {
  emit('change', filteredCountries.value.find(el => el.fips === selected.value)?.phoneCode ?? 972)
})

</script>

<style>
.dropdown:focus-within{
  outline: 1px solid #000000;
  border-radius: 8px;
}
</style>